// Live device wiring shared by the Sensors + Actuators pages (R-049). Both pages fan out over
// many locations, but the iot-live WebSocket is per PARENT room (a grow room's socket also
// carries its racks' frames; plumbing rooms are their own). So we open one socket per unique
// parent and multiplex their frames to the page. Event-driven, no polling — MQTT is the source
// of truth; the socket sends a retained snapshot on connect, then live deltas.
import { useEffect, useState } from 'preact/hooks';
import { useLiveRoom } from './useLiveRoom';
import type { LocationRef, WsEvent, WsKind } from '../domains/iot/iot.types';

/** The unique parent-room sockets to open for a set of location refs (racks collapse into their
 *  grow-room socket; a rack ref carries its parent room code in `room`). */
export function parentSockets(refs: LocationRef[]): { kind: WsKind; code: string }[] {
  const seen = new Map<string, { kind: WsKind; code: string }>();
  for (const ref of refs) {
    const kind: WsKind = ref.kind === 'plumbing_room' ? 'plumbing_room' : 'grow_room';
    const key = `${kind}:${ref.room}`;
    if (!seen.has(key)) seen.set(key, { kind, code: ref.room });
  }
  return [...seen.values()];
}

function OneRoom({ kind, code, onEvent }: { kind: WsKind; code: string; onEvent: (e: WsEvent) => void }) {
  useLiveRoom(kind, code, { onEvent });
  return null;
}

/** Renders one (invisible) live socket per parent room, forwarding every frame to `onEvent`. */
export function LiveRooms({
  parents,
  onEvent,
}: {
  parents: { kind: WsKind; code: string }[];
  onEvent: (e: WsEvent) => void;
}) {
  return (
    <>
      {parents.map((p) => (
        <OneRoom key={`${p.kind}:${p.code}`} kind={p.kind} code={p.code} onEvent={onEvent} />
      ))}
    </>
  );
}

/** A client-side clock that re-renders every `ms` so relative times / freshness stay honest
 *  without polling the server (a stopped sensor must still be able to age into "stale"). */
export function useNow(ms = 10_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return now;
}
